import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Upload, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { absolutize } from '../../components/MediaUrlsInput';
import TranslationsEditor from '../../components/TranslationsEditor';
import BulkTranslateButton from '../../components/BulkTranslateButton';
import DefaultsControls from '../../components/DefaultsControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_OPTIONS = ['Globe', 'Heart', 'Users', 'Award', 'Compass', 'Map', 'Mountain', 'Plane', 'Star', 'Smile', 'Camera', 'Sun'];

const emptyValue = { icon: 'Globe', title: '', description: '' };

const AboutManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [data, setData] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/about`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load About content'))
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const updateParagraph = (idx, val) => {
    const next = [...(data.story_paragraphs || [])];
    next[idx] = val;
    update({ story_paragraphs: next });
  };

  const addParagraph = () =>
    update({ story_paragraphs: [...(data.story_paragraphs || []), ''] });

  const removeParagraph = (idx) =>
    update({ story_paragraphs: (data.story_paragraphs || []).filter((_, i) => i !== idx) });

  const updateValue = (idx, patch) => {
    const next = [...(data.values || [])];
    next[idx] = { ...next[idx], ...patch };
    update({ values: next });
  };

  const addValue = () => update({ values: [...(data.values || []), { ...emptyValue }] });
  const removeValue = (idx) => update({ values: (data.values || []).filter((_, i) => i !== idx) });

  const handleStoryImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error('Image too large (max 200 MB).');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data?.url) {
        update({ story_image: res.data.url });
        toast.success('Image uploaded');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Strip blank paragraphs and incomplete values before saving
      const payload = {
        ...data,
        story_paragraphs: (data.story_paragraphs || []).map((p) => p.trim()).filter(Boolean),
        values: (data.values || []).filter((v) => v.title.trim() && v.description.trim()),
      };
      await axios.put(`${API}/about`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('About page saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <p className="text-maroon/60">Loading…</p>;
  }

  return (
    <div data-testid="about-manager" className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon flex items-center gap-2">
            <FileText size={28} className="text-terracotta" />
            About Page
          </h2>
          <p className="text-maroon/60 text-sm mt-1">Edit the content shown at /about. Changes apply immediately on save.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DefaultsControls
            section="about"
            reseedConfirm={
              'Restore the About page from your last saved snapshot?\n\n' +
              'This will overwrite the hero, mission, story, values, stats, and translations. ' +
              'If no snapshot has been saved yet, falls back to the original factory copy.'
            }
            onComplete={() => {
              axios.get(`${API}/about`)
                .then((res) => setData(res.data))
                .catch(() => {});
            }}
            reseedTestId="reseed-about-btn"
            snapshotTestId="snapshot-about-btn"
          />
          <Button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-about-btn">
            <Save size={16} />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Hero</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Headline</Label>
            <Input
              value={data.hero_title}
              onChange={(e) => update({ hero_title: e.target.value })}
              className="input-field"
              data-testid="about-hero-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Subheading</Label>
            <Textarea
              value={data.hero_subtitle}
              onChange={(e) => update({ hero_subtitle: e.target.value })}
              rows={3}
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              data-testid="about-hero-subtitle"
            />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Our Story</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Section Title</Label>
            <Input
              value={data.story_title}
              onChange={(e) => update({ story_title: e.target.value })}
              className="input-field"
              data-testid="about-story-title"
            />
          </div>

          <div className="space-y-2">
            <Label>Image</Label>
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              {data.story_image && (
                <img
                  src={absolutize(data.story_image)}
                  alt="Story"
                  className="w-32 h-24 object-cover rounded-sm border border-maroon/15 flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-1 w-full space-y-2">
                <Input
                  value={data.story_image}
                  onChange={(e) => update({ story_image: e.target.value })}
                  className="input-field"
                  placeholder="Paste an image URL or upload a file..."
                  data-testid="about-story-image-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="border-maroon/30 text-maroon hover:bg-maroon/5"
                  data-testid="about-story-image-upload-btn"
                >
                  <Upload size={16} className="mr-1" />
                  {uploading ? 'Uploading…' : 'Upload Image'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleStoryImageUpload(e.target.files?.[0])}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Paragraphs</Label>
            {(data.story_paragraphs || []).map((p, idx) => (
              <div key={idx} className="flex gap-2 items-start" data-testid={`about-story-p-${idx}`}>
                <Textarea
                  value={p}
                  onChange={(e) => updateParagraph(idx, e.target.value)}
                  rows={3}
                  className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeParagraph(idx)}
                  className="p-2 text-coral hover:bg-coral/10 rounded-sm flex-shrink-0"
                  aria-label="Remove paragraph"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addParagraph} className="border-maroon/20" data-testid="add-about-paragraph-btn">
              <Plus size={14} className="mr-1" /> Add Paragraph
            </Button>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Core Values</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Section Title</Label>
            <Input
              value={data.values_title}
              onChange={(e) => update({ values_title: e.target.value })}
              className="input-field"
              data-testid="about-values-title"
            />
          </div>

          {(data.values || []).map((v, idx) => (
            <div key={idx} className="border border-maroon/15 rounded-sm p-4 space-y-3 bg-sand/20" data-testid={`about-value-${idx}`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Icon</Label>
                  <select
                    value={v.icon}
                    onChange={(e) => updateValue(idx, { icon: e.target.value })}
                    className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
                  >
                    {ICON_OPTIONS.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={v.title}
                    onChange={(e) => updateValue(idx, { title: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={v.description}
                  onChange={(e) => updateValue(idx, { description: e.target.value })}
                  rows={2}
                  className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                />
              </div>
              <button
                type="button"
                onClick={() => removeValue(idx)}
                className="text-coral hover:bg-coral/10 px-3 py-1 rounded-sm text-sm font-semibold inline-flex items-center gap-1"
                data-testid={`remove-about-value-${idx}`}
              >
                <Trash2 size={14} /> Remove value
              </button>
            </div>
          ))}

          <Button type="button" variant="outline" size="sm" onClick={addValue} className="border-maroon/20" data-testid="add-about-value-btn">
            <Plus size={14} className="mr-1" /> Add Value
          </Button>
        </div>
      </section>

      {/* Mission */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Mission</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Section Title</Label>
            <Input
              value={data.mission_title}
              onChange={(e) => update({ mission_title: e.target.value })}
              className="input-field"
              data-testid="about-mission-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Mission Statement</Label>
            <Textarea
              value={data.mission_text}
              onChange={(e) => update({ mission_text: e.target.value })}
              rows={4}
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              data-testid="about-mission-text"
            />
          </div>
        </div>
      </section>

      {/* Translations */}
      <section className="card mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-heading font-bold text-xl text-maroon">Translations</h3>
          <BulkTranslateButton
            scope="about"
            onComplete={() => {
              // refetch content so the new ES values appear in the editor
              const token = localStorage.getItem('token');
              axios.get(`${API}/about`, { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => setData(res.data))
                .catch(() => {});
            }}
          />
        </div>
        <TranslationsEditor
          translations={data.translations || {}}
          onChange={(translations) => update({ translations })}
          sourceContent={{
            hero_title: data.hero_title,
            hero_subtitle: data.hero_subtitle,
            story_title: data.story_title,
            story_paragraphs: data.story_paragraphs,
            values_title: data.values_title,
            values: data.values,
            mission_title: data.mission_title,
            mission_text: data.mission_text,
          }}
          fields={[
            { key: 'hero_title', label: 'Hero Title' },
            { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
            { key: 'story_title', label: 'Story Title' },
            { key: 'values_title', label: 'Values Section Title' },
            { key: 'mission_title', label: 'Mission Title' },
            { key: 'mission_text', label: 'Mission Text', type: 'textarea' },
          ]}
          languages={['es']}
        />
        <p className="text-xs text-maroon/50 mt-3">
          Tip: click <strong>Translate to Spanish</strong> to auto-fill all Spanish fields, including
          paragraphs and core values. Then expand the section to review and edit any wording.
        </p>
      </section>

      <div className="flex justify-end mb-12">
        <Button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2">
          <Save size={16} />
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AboutManager;
